import React, { useState } from 'react';
import { 
  X, Download, Printer, BookOpen, ShieldCheck, Award, 
  User, Calendar, Clock, MapPin, ChevronRight, ChevronLeft, 
  CheckCircle2, FileText, Sparkles, Share2, Layers, Bookmark,
  ExternalLink, Code2, Database, Laptop, Check, Copy
} from 'lucide-react';
import { getCourseEbookDossier } from '../../data/courseEbookData';

export const CourseEbookModal = ({ isOpen, onClose, courseCode = 'BUS 911' }) => {
  const [activeSection, setActiveSection] = useState('cover'); // 'cover' | 'foreword' | 'curriculum' | 'chapters' | 'readings' | 'software' | 'assessment'
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const [copiedLink, setCopiedLink] = useState(null);

  if (!isOpen) return null;

  const ebook = getCourseEbookDossier(courseCode);
  const isEkere = ebook.courseCode === 'BUS 911' || ebook.courseCode === 'DAF 911' || ebook.courseCode.includes('911');

  const handlePrintPdf = () => {
    // Open print dialog with printable media styles
    window.print();
  };

  const copyToClipboard = (text, id) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedLink(id);
      setTimeout(() => setCopiedLink(null), 2500);
    }
  };

  const handleDownloadStandaloneHtml = () => {
    // Generate downloadable, standalone luxury HTML eBook file with embedded styles & live hyperlinks
    const htmlContent = `<!DOCTYPE html>
<html lang="en-GB">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${ebook.courseCode}: ${ebook.courseTitle} - Executive Course Handbook | AKSU Business School</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Alegreya+Sans:wght@400;500;700;900&family=Alegreya+Sans+SC:wght@700;900&family=Cinzel:wght@600;700;900&family=Georgia:ital,wght@0,400;0,700;1,400&family=JetBrains+Mono:wght@400;600&display=swap');
    
    :root {
      --primary: #06182B;
      --secondary: #0B2644;
      --gold: #B87A28;
      --gold-light: #D4AF37;
      --gold-dark: #7A470E;
      --text: #1E293B;
      --text-muted: #64748B;
      --bg: #FDFBF7;
      --card-bg: #FFFFFF;
      --border: #E2E8F0;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Georgia, serif; line-height: 1.8; color: var(--text); background: var(--bg); padding: 40px 20px; }
    .container { max-width: 960px; margin: 0 auto; }
    
    /* Luxury Cover */
    .cover {
      background: linear-gradient(135deg, #030F1C 0%, #06182B 50%, #0B2644 100%);
      color: #FFFFFF;
      border: 3px solid var(--gold);
      border-radius: 24px;
      padding: 60px 40px;
      text-align: center;
      margin-bottom: 50px;
      box-shadow: 0 20px 40px rgba(0,0,0,0.25);
      position: relative;
      overflow: hidden;
    }
    .cover::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 6px;
      background: linear-gradient(90deg, #7A470E, #D4AF37, #B87A28, #7A470E);
    }
    .logos-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 30px;
      margin-bottom: 30px;
    }
    .logo-frame {
      width: 90px;
      height: 90px;
      background: rgba(255, 255, 255, 0.08);
      border: 2px solid var(--gold-light);
      border-radius: 20px;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 16px rgba(0,0,0,0.3);
    }
    .logo-frame img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
    .inst-title {
      font-family: 'Alegreya Sans SC', sans-serif;
      font-size: 24px;
      font-weight: 900;
      letter-spacing: 2px;
      color: var(--gold-light);
      text-transform: uppercase;
    }
    .inst-sub {
      font-family: 'Alegreya Sans', sans-serif;
      font-size: 14px;
      color: #94A3B8;
      letter-spacing: 1px;
      margin-top: 4px;
    }
    .course-badge {
      display: inline-block;
      margin: 25px 0 15px 0;
      padding: 6px 20px;
      background: rgba(122, 71, 14, 0.6);
      border: 1px solid var(--gold-light);
      border-radius: 30px;
      color: #FDE68A;
      font-family: 'JetBrains Mono', monospace;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 1px;
    }
    h1 {
      font-family: 'Alegreya Sans', sans-serif;
      font-size: 42px;
      font-weight: 900;
      line-height: 1.2;
      color: #FFFFFF;
      margin: 10px 0 15px 0;
    }
    .motto {
      font-size: 16px;
      color: #CBD5E1;
      font-style: italic;
      margin-bottom: 30px;
    }
    
    /* Facilitator Card */
    .facilitator-card {
      background: rgba(3, 15, 28, 0.85);
      border: 1px solid rgba(212, 175, 55, 0.5);
      border-radius: 18px;
      padding: 24px;
      margin: 30px auto 0 auto;
      max-width: 680px;
      display: flex;
      align-items: center;
      gap: 24px;
      text-align: left;
    }
    .facilitator-avatar {
      width: 100px;
      height: 100px;
      border-radius: 16px;
      object-fit: cover;
      border: 2px solid var(--gold-light);
      box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      flex-shrink: 0;
    }
    .fac-label {
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      font-weight: 700;
      color: var(--gold-light);
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }
    .fac-name {
      font-family: 'Alegreya Sans SC', sans-serif;
      font-size: 22px;
      font-weight: 900;
      color: #FFFFFF;
      margin: 3px 0;
    }
    .fac-rank {
      font-size: 13px;
      font-weight: 700;
      color: #FDE68A;
    }
    .fac-meta {
      font-size: 12px;
      color: #94A3B8;
      margin-top: 4px;
      font-family: 'JetBrains Mono', monospace;
    }

    /* Section Styling */
    .section-card {
      background: var(--card-bg);
      border: 1px solid var(--border);
      border-radius: 20px;
      padding: 40px;
      margin-bottom: 35px;
      box-shadow: 0 6px 18px rgba(0,0,0,0.04);
    }
    .section-header {
      border-bottom: 2px solid var(--gold);
      padding-bottom: 12px;
      margin-bottom: 25px;
    }
    .section-pre {
      font-family: 'Alegreya Sans SC', sans-serif;
      font-size: 12px;
      font-weight: 700;
      color: var(--gold-dark);
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }
    h2 {
      font-family: 'Alegreya Sans SC', sans-serif;
      font-size: 28px;
      font-weight: 900;
      color: var(--secondary);
      line-height: 1.25;
      margin-top: 2px;
    }
    .prose p {
      font-size: 15px;
      line-height: 1.8;
      color: #334155;
      margin-bottom: 18px;
    }
    
    /* 13-Week Syllabus Table */
    .week-row {
      background: #F8FAFC;
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 20px;
      margin-bottom: 16px;
      transition: all 0.2s ease;
    }
    .week-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    .week-badge {
      background: var(--secondary);
      color: #FDE68A;
      font-family: 'JetBrains Mono', monospace;
      font-size: 12px;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 8px;
    }
    .week-title {
      font-family: 'Alegreya Sans', sans-serif;
      font-size: 18px;
      font-weight: 700;
      color: var(--secondary);
      margin-bottom: 6px;
    }
    .subtopics-list {
      margin-left: 20px;
      font-size: 13.5px;
      color: #475569;
      margin-bottom: 10px;
    }
    .subtopics-list li { margin-bottom: 4px; }
    .week-readings {
      background: #FFFBEB;
      border-left: 3px solid var(--gold);
      padding: 8px 14px;
      border-radius: 0 8px 8px 0;
      font-size: 12.5px;
      color: #78350F;
      font-family: 'JetBrains Mono', monospace;
    }

    /* Chapter Content */
    .chapter-number {
      font-family: 'Alegreya Sans SC', sans-serif;
      font-size: 14px;
      font-weight: 900;
      color: var(--gold-dark);
      letter-spacing: 2px;
      text-transform: uppercase;
    }
    .chapter-sub {
      font-size: 14px;
      color: var(--text-muted);
      font-style: italic;
      margin-top: 4px;
    }
    .takeaways-box {
      background: #F1F5F9;
      border-left: 4px solid var(--secondary);
      padding: 20px;
      border-radius: 0 12px 12px 0;
      margin-top: 25px;
    }
    .takeaways-box strong {
      font-family: 'Alegreya Sans SC', sans-serif;
      color: var(--secondary);
      font-size: 14px;
      display: block;
      margin-bottom: 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .takeaways-box ul {
      margin-left: 20px;
      font-size: 14px;
      color: #334155;
    }
    .takeaways-box li { margin-bottom: 6px; }

    /* Bibliography & Hyperlinked Texts */
    .book-card {
      background: #FFFFFF;
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 22px;
      margin-bottom: 18px;
      box-shadow: 0 3px 10px rgba(0,0,0,0.03);
    }
    .book-title {
      font-size: 16px;
      font-weight: bold;
      color: var(--secondary);
      line-height: 1.4;
    }
    .book-authors {
      font-size: 13.5px;
      color: #475569;
      margin: 4px 0;
    }
    .book-isbn {
      display: inline-block;
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      background: #FEF3C7;
      color: #92400E;
      padding: 3px 8px;
      border-radius: 6px;
      font-weight: 700;
      margin-right: 8px;
    }
    .book-links {
      margin-top: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .book-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 5px 12px;
      background: #0B2644;
      color: #FFFFFF !important;
      text-decoration: none;
      font-family: 'Alegreya Sans', sans-serif;
      font-weight: 700;
      font-size: 12px;
      border-radius: 8px;
    }
    .book-link.alt {
      background: #F1F5F9;
      color: #0B2644 !important;
      border: 1px solid #CBD5E1;
    }

    /* Print Formatting */
    @media print {
      body { background: #FFFFFF; padding: 0; }
      .container { max-width: 100%; }
      .cover { page-break-after: always; box-shadow: none; border-radius: 0; }
      .section-card { box-shadow: none; border-radius: 0; border: none; padding: 20px 0; page-break-after: always; }
      .book-link { text-decoration: underline; background: none !important; color: #000000 !important; }
    }
  </style>
</head>
<body>
  <div class="container">
    
    <!-- COVER PAGE -->
    <div class="cover">
      <div class="logos-row">
        <!-- Official University Crest -->
        <div class="logo-frame" title="Official Crest - Akwa Ibom State University">
          <img src="/aksu-university-crest-official.jpg" alt="AKSU University Crest" />
        </div>
        <!-- Official Business School Shield -->
        <div class="logo-frame" title="Official Shield - AKSU Business School">
          <img src="/aksu-business-school-shield-official.png" alt="AKSU Business School Shield" />
        </div>
      </div>

      <div class="inst-title">Akwa Ibom State University Business School</div>
      <div class="inst-sub">MAIN CAMPUS, IKOT AKPADEN · SENATE APPROVED DOCTORAL CURRICULUM</div>
      
      <div class="course-badge">${ebook.crossListing || ebook.courseCode} · ${ebook.credits} CREDIT UNITS · ${ebook.semester}</div>
      <h1>${ebook.courseTitle}</h1>
      <p class="motto">"Knowledge, Enterprise and Executive Leadership for Sustainable Industrialisation"</p>

      <!-- Appointed Course Facilitator -->
      <div class="facilitator-card">
        <img src="${ebook.facilitator.avatar || '/dr-daniel-ekere.png'}" alt="${ebook.facilitator.name}" class="facilitator-avatar" />
        <div>
          <div class="fac-label">Appointed Course Facilitator & Lecturer</div>
          <div class="fac-name">${ebook.facilitator.salutation}</div>
          <div class="fac-rank">${ebook.facilitator.rank} · ${ebook.facilitator.qualifications}</div>
          <div class="fac-meta">Office: ${ebook.facilitator.office}</div>
          <div class="fac-meta">Official Email: ${ebook.facilitator.email} · Phone: ${ebook.facilitator.phone}</div>
        </div>
      </div>
    </div>

    <!-- FOREWORD & OVERVIEW -->
    <div class="section-card">
      <div class="section-header">
        <div class="section-pre">Institutional Mandate</div>
        <h2>Directorate Foreword & Academic Philosophy</h2>
      </div>
      <div class="prose">
        <p>${ebook.foreword}</p>
        <p>${ebook.overview}</p>
      </div>

      <div style="background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 14px; padding: 20px; margin-top: 20px;">
        <strong style="color: #78350F; font-size: 14px; display: block; margin-bottom: 8px;">Executive Learning Outcomes:</strong>
        <ul style="margin-left: 20px; font-size: 13.5px; color: #451A03;">
          ${ebook.learningOutcomes.map(lo => `<li style="margin-bottom: 6px;">${lo}</li>`).join('')}
        </ul>
      </div>
    </div>

    <!-- 13-WEEK DETAILED SYLLABUS MATRIX -->
    <div class="section-card">
      <div class="section-header">
        <div class="section-pre">Senate Approved Schedule</div>
        <h2>Full 13-Week Doctoral Syllabus Matrix</h2>
      </div>

      ${ebook.modules.map((m, idx) => `
        <div class="week-row">
          <div class="week-top">
            <span class="week-badge">${m.number || `Week ${idx + 1}`}</span>
            <span style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #64748B;">Duration: ${m.duration || "180 mins"}</span>
          </div>
          <div class="week-title">${m.title}</div>
          ${m.majorTopic ? `<div style="font-size: 13px; font-weight: bold; color: #475569; margin-bottom: 8px;">Major Topic: ${m.majorTopic}</div>` : ''}
          ${m.minorTopics ? `
            <ul class="subtopics-list">
              ${m.minorTopics.map(sub => `<li>${sub}</li>`).join('')}
            </ul>
          ` : `<p style="font-size: 13.5px; color: #475569; margin-bottom: 10px;">${m.keyTakeaway}</p>`}
          ${m.readings ? `
            <div class="week-readings">
              <strong>Assigned Readings:</strong> ${m.readings.join('; ')}
            </div>
          ` : ''}
        </div>
      `).join('')}
    </div>

    <!-- EBOOK CHAPTERS -->
    ${ebook.chapters.map(ch => `
      <div class="section-card">
        <div class="section-header">
          <div class="chapter-number">${ch.number}</div>
          <h2>${ch.title}</h2>
          <div class="chapter-sub">${ch.subtitle}</div>
        </div>
        <div class="prose">
          ${ch.content.map(p => `<p>${p}</p>`).join('')}
        </div>
        <div class="takeaways-box">
          <strong>Core Executive Takeaways:</strong>
          <ul>
            ${ch.keyTakeaways.map(kt => `<li>${kt}</li>`).join('')}
          </ul>
        </div>
      </div>
    `).join('')}

    <!-- HYPERLINKED REFERENCES -->
    <div class="section-card">
      <div class="section-header">
        <div class="section-pre">Prescribed Doctoral Literature</div>
        <h2>Recommended Reference Textbooks & Verified Publisher Portals</h2>
      </div>
      <p style="font-size: 14px; color: #64748B; margin-bottom: 20px;">
        All references listed below are hyperlinked directly to their official publisher catalogues, companion datasets, and institutional library records.
      </p>

      ${(ebook.generalReferences || []).map(r => `
        <div class="book-card">
          <div class="book-title">${r.title} (${r.edition})</div>
          <div class="book-authors">${r.author} (${r.year}) · <em>${r.publisher}</em></div>
          <div style="margin: 6px 0;">
            <span class="book-isbn">ISBN: ${r.isbn}</span>
            <span style="font-size: 12px; color: #64748B;">${r.scope}</span>
          </div>
          <div class="book-links">
            ${r.url ? `<a href="${r.url}" target="_blank" rel="noopener noreferrer" class="book-link">Official Publisher Link →</a>` : ''}
            ${r.altUrl ? `<a href="${r.altUrl}" target="_blank" rel="noopener noreferrer" class="book-link alt">WorldCat / Companion Site →</a>` : ''}
            ${r.companionUrl ? `<a href="${r.companionUrl}" target="_blank" rel="noopener noreferrer" class="book-link alt">Author Datasets & Code →</a>` : ''}
          </div>
        </div>
      `).join('')}
    </div>

    <!-- ECONOMETRIC SOFTWARE SUITE -->
    <div class="section-card">
      <div class="section-header">
        <div class="section-pre">Computational Laboratories</div>
        <h2>Econometric Software Suite & Script Repositories</h2>
      </div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px;">
        ${(ebook.softwareTools || []).map(tool => `
          <div style="background: #F8FAFC; border: 1px solid var(--border); border-radius: 12px; padding: 18px;">
            <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px;">
              <strong style="color: var(--secondary); font-size: 15px;">${tool.name}</strong>
              <span style="font-family: 'JetBrains Mono', monospace; font-size: 10px; background: #E2E8F0; padding: 2px 6px; border-radius: 4px;">${tool.category}</span>
            </div>
            <p style="font-size: 12.5px; color: #475569; margin-bottom: 12px;">${tool.desc}</p>
            <a href="${tool.url}" target="_blank" rel="noopener noreferrer" class="book-link" style="font-size: 11px;">Open Official Documentation →</a>
          </div>
        `).join('')}
      </div>
    </div>

    <!-- SENATE ASSESSMENT RUBRIC -->
    <div class="section-card">
      <div class="section-header">
        <div class="section-pre">Senate Grading Scheme</div>
        <h2>Continuous Assessment & Examination Weightings</h2>
      </div>
      <div style="display: grid; grid-template-columns: 1fr; gap: 10px;">
        ${ebook.assessmentStructure.map(asg => `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; background: #F8FAFC; border: 1px solid var(--border); border-radius: 10px;">
            <span style="font-size: 13.5px; font-weight: 600; color: #1E293B;">${asg.component}</span>
            <span style="background: var(--secondary); color: #FFFFFF; font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: bold; padding: 4px 10px; border-radius: 6px;">${asg.weight}</span>
          </div>
        `).join('')}
      </div>
    </div>

    <!-- FOOTER -->
    <footer style="text-align: center; padding: 40px 0; font-size: 12px; color: #94A3B8; border-top: 1px solid #CBD5E1;">
      <p><strong>Akwa Ibom State University Business School</strong> · NUC Accredited Academic Authority</p>
      <p style="margin-top: 4px;">Official Portal: <a href="https://businessschool.aksu.edu.ng" style="color: var(--gold-dark);">https://businessschool.aksu.edu.ng</a> · Obio Akpa Campus & Uyo Liaison Office</p>
    </footer>

  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ebook.courseCode.replace(/[\/\s]+/g, '_')}_Official_eBook_Handbook.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 md:p-6 animate-in fade-in duration-200">
      
      {/* Modal Container */}
      <div className="relative w-full max-w-5xl bg-[#FDFBF7] rounded-3xl shadow-2xl border-2 border-[#7A470E] overflow-hidden flex flex-col max-h-[94vh]">
        
        {/* ======================================================== */}
        {/* TOP TOOLBAR: Dual Official Logos & Actions               */}
        {/* ======================================================== */}
        <div className="bg-[#06182B] text-white px-4 sm:px-6 py-3 border-b-2 border-[#7A470E] flex items-center justify-between gap-3 shrink-0 print:hidden">
          
          <div className="flex items-center space-x-3">
            {/* Dual Official University & Business School Logos */}
            <div className="flex items-center space-x-2 shrink-0">
              <img 
                src={ebook.officialLogos?.universityCrest || "/aksu-university-crest-official.jpg"} 
                alt="AKSU University Crest" 
                className="w-8 h-8 rounded-lg object-contain bg-white/10 p-0.5 border border-amber-400/60 shadow-xs" 
                title="Akwa Ibom State University Official Crest"
              />
              <img 
                src={ebook.officialLogos?.businessSchoolShield || "/aksu-business-school-shield-official.png"} 
                alt="AKSU Business School Shield" 
                className="w-8 h-8 rounded-lg object-contain bg-white/10 p-0.5 border border-amber-400/60 shadow-xs" 
                title="AKSU Business School Official Shield"
              />
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <span className="font-sc font-black text-xs sm:text-sm text-white truncate">
                  AKSU Business School
                </span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#7A470E] text-amber-200 font-bold shrink-0">
                  Official Luxury eBook
                </span>
              </div>
              <div className="text-[11px] text-amber-300/90 font-mono truncate max-w-xs sm:max-w-md">
                {ebook.crossListing || ebook.courseCode}: {ebook.courseTitle}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Download as PDF Button */}
            <button
              onClick={handlePrintPdf}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#7A470E] to-[#B87A28] hover:from-[#B87A28] hover:to-[#D4AF37] text-white font-sc font-bold text-xs transition-all shadow-sm cursor-pointer hover:scale-105 shrink-0"
              title="Save or Print as High-Resolution PDF"
            >
              <Printer className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Print / Save PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>

            {/* Offline Standalone HTML eBook Package */}
            <button
              onClick={handleDownloadStandaloneHtml}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#133863] text-amber-200 font-sc font-bold text-xs border border-amber-400/40 transition-colors cursor-pointer shrink-0"
              title="Download Standalone HTML eBook with Hyperlinks"
            >
              <Download className="w-3.5 h-3.5 text-amber-300" />
              <span className="hidden sm:inline">HTML eBook</span>
            </button>

            {/* Close Modal */}
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl bg-slate-800 hover:bg-rose-900/80 text-slate-300 hover:text-white transition-colors cursor-pointer ml-1 shrink-0"
              title="Close eBook Reader"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* NAVIGATION PILL BAR                                      */}
        {/* ======================================================== */}
        <div className="bg-[#030F1C] px-4 py-2 border-b border-slate-800 flex items-center space-x-2 overflow-x-auto no-scrollbar shrink-0 print:hidden text-xs">
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider shrink-0 mr-1">
            Contents:
          </span>
          {[
            { id: 'cover', label: 'Front Cover' },
            { id: 'foreword', label: 'Senate Foreword' },
            { id: 'curriculum', label: isEkere ? '13-Week Syllabus' : 'Modular Syllabus' },
            { id: 'chapters', label: `eBook Chapters (${ebook.chapters.length})` },
            { id: 'readings', label: 'Hyperlinked References (5 Books)' },
            { id: 'software', label: 'Software Laboratories' },
            { id: 'assessment', label: 'Grading Rubric' }
          ].map(sec => (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              className={`px-3 py-1 rounded-lg font-sc font-bold text-xs shrink-0 transition-all cursor-pointer ${
                activeSection === sec.id
                  ? 'bg-[#B87A28] text-white shadow-xs ring-1 ring-amber-300'
                  : 'bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {sec.label}
            </button>
          ))}
        </div>

        {/* ======================================================== */}
        {/* EBOOK CONTENT VIEWPORT                                   */}
        {/* ======================================================== */}
        <div id="aksu-ebook-print-stage" className="flex-1 overflow-y-auto p-4 sm:p-8 md:p-10 space-y-8 text-slate-800">
          
          {/* SECTION 1: LUXURY FRONT COVER */}
          {(activeSection === 'cover' || typeof window === 'undefined') && (
            <div className="max-w-3xl mx-auto rounded-3xl p-6 sm:p-12 text-center text-white relative overflow-hidden shadow-2xl border-4 border-[#B87A28] bg-gradient-to-b from-[#030F1C] via-[#06182B] to-[#0B2644]">
              {/* Foil Background Accents */}
              <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-[#7A470E] via-[#D4AF37] to-[#B87A28]" />
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#B87A28]/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#7A470E]/25 rounded-full blur-3xl pointer-events-none" />

              {/* DUAL OFFICIAL LOGOS IN CENTRE HEADER */}
              <div className="relative mb-6">
                <div className="flex items-center justify-center space-x-6 sm:space-x-8">
                  {/* Akwa Ibom State University Crest Cog */}
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/10 p-2 border-2 border-[#D4AF37] shadow-xl flex items-center justify-center">
                      <img 
                        src="/aksu-university-crest-official.jpg" 
                        alt="AKSU Official Crest" 
                        className="w-full h-full object-contain" 
                      />
                    </div>
                    <span className="text-[10px] font-mono text-amber-300 font-bold uppercase tracking-wider mt-2">
                      University Crest
                    </span>
                  </div>

                  <div className="h-14 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />

                  {/* AKSU Business School Shield */}
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/10 p-2 border-2 border-[#D4AF37] shadow-xl flex items-center justify-center">
                      <img 
                        src="/aksu-business-school-shield-official.png" 
                        alt="AKSU Business School Official Shield" 
                        className="w-full h-full object-contain" 
                      />
                    </div>
                    <span className="text-[10px] font-mono text-amber-300 font-bold uppercase tracking-wider mt-2">
                      Business School
                    </span>
                  </div>
                </div>
              </div>

              {/* Title & Domain */}
              <div className="space-y-2">
                <span className="text-xs sm:text-sm uppercase tracking-widest font-black text-[#D4AF37] font-sc block">
                  Akwa Ibom State University Business School
                </span>
                <span className="text-[10px] text-slate-400 font-mono tracking-wider block">
                  Main Campus · Ikot Akpaden, Mkpat Enin LGA · businessschool.aksu.edu.ng
                </span>
                <div className="h-0.5 w-32 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent my-3" />
              </div>

              {/* Course Title Headline */}
              <div className="my-6 space-y-2">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-mono font-bold bg-[#7A470E]/70 text-amber-200 border border-[#B87A28]">
                  {ebook.crossListing || ebook.courseCode} · {ebook.credits} Credit Units
                </span>
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-black font-sc text-white tracking-tight leading-tight">
                  {ebook.courseTitle}
                </h1>
                <p className="text-xs sm:text-sm text-slate-300 font-serif italic max-w-xl mx-auto pt-1">
                  Official Executive Course Handbook, 13-Week Syllabi & Doctoral Reading Dossier
                </p>
              </div>

              {/* Degree Badge */}
              <div className="my-4">
                <span className="px-4 py-1.5 rounded-xl bg-white/10 text-white font-sc font-bold text-xs border border-white/20">
                  {ebook.programmeName} · Academic Session {ebook.academicSession || "2025/2026"}
                </span>
              </div>

              {/* Course Facilitator Profile Box with Photo */}
              <div className="mt-8 pt-6 border-t border-slate-800 max-w-lg mx-auto bg-[#030F1C]/85 rounded-2xl p-4 sm:p-5 border border-[#B87A28]/50 text-left flex items-center space-x-4 shadow-xl">
                <img 
                  src={ebook.facilitator.avatar || "/dr-daniel-ekere.png"} 
                  alt={ebook.facilitator.name} 
                  className="w-18 h-18 sm:w-22 sm:h-22 rounded-2xl object-cover object-top border-2 border-[#D4AF37] shadow-lg shrink-0 bg-slate-800" 
                />
                <div className="space-y-1 min-w-0">
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-[#D4AF37] block">
                    Appointed Course Facilitator & Lecturer
                  </span>
                  <h3 className="font-sc font-black text-base sm:text-lg text-white truncate">
                    {ebook.facilitator.salutation}
                  </h3>
                  <p className="text-xs text-[#B87A28] font-sc font-bold">
                    {ebook.facilitator.rank}
                  </p>
                  <p className="text-[11px] text-slate-300 font-mono">
                    {ebook.facilitator.qualifications}
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono truncate">
                    Office: {ebook.facilitator.office}
                  </p>
                </div>
              </div>

              {/* Action Buttons inside Cover */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => setActiveSection('curriculum')}
                  className="px-4 py-2 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-bold font-sc flex items-center space-x-1.5 transition-colors cursor-pointer"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  <span>Inspect 13-Week Syllabus</span>
                </button>

                <button
                  onClick={() => setActiveSection('readings')}
                  className="px-4 py-2 rounded-xl bg-[#0B2644] hover:bg-[#133562] text-amber-200 text-xs font-bold font-sc flex items-center space-x-1.5 border border-amber-400/40 transition-colors cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>View Hyperlinked Books (5)</span>
                </button>

                <button
                  onClick={handleDownloadStandaloneHtml}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold font-sc flex items-center space-x-1.5 transition-colors cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5 text-amber-300" />
                  <span>Download Standalone File</span>
                </button>
              </div>

              {/* Seal Footer */}
              <div className="mt-8 pt-4 flex flex-wrap items-center justify-between text-[11px] text-slate-400 font-mono border-t border-slate-800">
                <span className="flex items-center">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1 text-emerald-400" />
                  Senate Approved Curriculum
                </span>
                <span>NUC Accredited Academic Licence</span>
              </div>
            </div>
          )}

          {/* SECTION 2: FOREWORD & OVERVIEW */}
          {(activeSection === 'foreword' || activeSection === 'cover') && (
            <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              <div className="border-b-2 border-[#7A470E] pb-3">
                <span className="text-[11px] font-bold text-[#7A470E] font-sc uppercase tracking-wider block">
                  Institutional Mandate
                </span>
                <h2 className="text-2xl font-black font-sc text-[#0B2644]">
                  Directorate Foreword & Academic Philosophy
                </h2>
              </div>

              <div className="prose text-sm text-slate-700 leading-relaxed font-serif space-y-4">
                <p className="first-letter:text-5xl first-letter:font-black first-letter:text-[#7A470E] first-letter:font-sc first-letter:mr-2 first-letter:float-left">
                  {ebook.foreword}
                </p>
                <p>
                  {ebook.overview}
                </p>
              </div>

              {/* Key Learning Outcomes */}
              <div className="bg-amber-50/60 rounded-2xl p-5 border border-amber-200 space-y-3">
                <h3 className="font-sc font-bold text-sm text-[#0B2644] flex items-center">
                  <Award className="w-4 h-4 mr-2 text-[#7A470E]" />
                  Executive Competency & Learning Outcomes:
                </h3>
                <ul className="space-y-2 text-xs text-slate-700">
                  {ebook.learningOutcomes.map((lo, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{lo}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* SECTION 3: 13-WEEK DETAILED SYLLABUS MATRIX */}
          {(activeSection === 'curriculum' || activeSection === 'cover') && (
            <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              <div className="border-b-2 border-[#7A470E] pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[11px] font-bold text-[#7A470E] font-sc uppercase tracking-wider block">
                    Curriculum Architecture
                  </span>
                  <h2 className="text-2xl font-black font-sc text-[#0B2644]">
                    {isEkere ? "13-Week Doctoral Syllabus Matrix" : "Modular Syllabus Architecture"}
                  </h2>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-[#0B2644] text-amber-200 self-start sm:self-auto">
                  {ebook.modules.length} Scheduled Units
                </span>
              </div>

              <p className="text-xs text-slate-600 font-sans">
                Each weekly seminar is delivered via alternate-weekend modular sessions (Saturdays 12:30 – 15:30), integrating mathematical theoretical exposition with hands-on computer laboratory demonstrations in Stata, R, and Python.
              </p>

              <div className="space-y-4">
                {ebook.modules.map((m, idx) => (
                  <div key={idx} className="p-4 sm:p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#7A470E] transition-colors space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono font-bold text-xs text-[#7A470E] bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                        {m.number || `Week ${idx + 1}`}
                      </span>
                      <span className="text-slate-500 font-mono text-[11px] flex items-center">
                        <Clock className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
                        {m.duration || "180 mins"}
                      </span>
                    </div>

                    <div>
                      <h3 className="font-sc font-bold text-base sm:text-lg text-slate-900">
                        {m.title}
                      </h3>
                      {m.majorTopic && (
                        <div className="text-xs font-bold text-[#0B2644] mt-0.5 font-sans">
                          Major Topic: {m.majorTopic}
                        </div>
                      )}
                    </div>

                    {/* Minor Topics */}
                    {m.minorTopics && m.minorTopics.length > 0 && (
                      <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-1.5">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold block">
                          Subtopics & Lab Exercises:
                        </span>
                        <ul className="space-y-1 text-xs text-slate-700">
                          {m.minorTopics.map((sub, sIdx) => (
                            <li key={sIdx} className="flex items-start space-x-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#7A470E] shrink-0 mt-1.5" />
                              <span>{sub}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Readings */}
                    {m.readings && m.readings.length > 0 && (
                      <div className="p-2.5 rounded-xl bg-amber-50/70 border-l-4 border-[#7A470E] text-[11px] text-[#7A470E] font-mono leading-relaxed">
                        <strong className="block text-[#0B2644] font-bold font-sans mb-0.5">Prescribed Weekly Readings:</strong>
                        {m.readings.join(' • ')}
                      </div>
                    )}

                    {/* Fallback takeaway */}
                    {!m.minorTopics && m.keyTakeaway && (
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        <strong>Executive Focus:</strong> {m.keyTakeaway}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 4: EBOOK CHAPTERS */}
          {(activeSection === 'chapters' || activeSection === 'cover') && (
            <div className="max-w-3xl mx-auto space-y-6">
              
              {/* Chapter Tabs for Interactive Reading */}
              <div className="bg-white rounded-2xl p-3 border border-slate-200 flex items-center space-x-2 overflow-x-auto no-scrollbar">
                {ebook.chapters.map((ch, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveChapterIndex(idx)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-sc font-bold shrink-0 transition-colors cursor-pointer ${
                      activeChapterIndex === idx 
                        ? 'bg-[#0B2644] text-white shadow-xs' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {ch.number}
                  </button>
                ))}
              </div>

              {/* Active Chapter Card */}
              {(() => {
                const ch = ebook.chapters[activeChapterIndex] || ebook.chapters[0];
                return (
                  <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
                    <div className="border-b-2 border-[#7A470E] pb-3">
                      <span className="text-[11px] font-bold text-[#7A470E] font-sc uppercase tracking-wider block">
                        {ch.number}
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644]">
                        {ch.title}
                      </h2>
                      <p className="text-xs text-slate-500 italic mt-1">
                        {ch.subtitle}
                      </p>
                    </div>

                    <div className="prose text-sm text-slate-700 leading-relaxed font-serif space-y-4">
                      {ch.content.map((p, pIdx) => (
                        <p key={pIdx}>{p}</p>
                      ))}
                    </div>

                    <div className="bg-slate-50 rounded-2xl p-5 border-l-4 border-[#7A470E] space-y-2">
                      <h4 className="font-sc font-bold text-xs text-[#0B2644] uppercase tracking-wider">
                        Core Methodological Takeaways:
                      </h4>
                      <ul className="space-y-1.5 text-xs text-slate-700">
                        {ch.keyTakeaways.map((kt, ktIdx) => (
                          <li key={ktIdx} className="flex items-start space-x-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#7A470E] shrink-0 mt-1.5" />
                            <span>{kt}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Chapter Navigation Buttons */}
                    <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                      <button
                        disabled={activeChapterIndex === 0}
                        onClick={() => setActiveChapterIndex(prev => Math.max(0, prev - 1))}
                        className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold font-sc flex items-center space-x-1 disabled:opacity-30 cursor-pointer"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Previous Chapter</span>
                      </button>

                      <span className="text-xs font-mono text-slate-400">
                        Chapter {activeChapterIndex + 1} of {ebook.chapters.length}
                      </span>

                      <button
                        disabled={activeChapterIndex === ebook.chapters.length - 1}
                        onClick={() => setActiveChapterIndex(prev => Math.min(ebook.chapters.length - 1, prev + 1))}
                        className="px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#133562] text-white text-xs font-bold font-sc flex items-center space-x-1 disabled:opacity-30 cursor-pointer"
                      >
                        <span>Next Chapter</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })()}

            </div>
          )}

          {/* SECTION 5: HYPERLINKED REFERENCES (5 TEXTBOOKS) */}
          {(activeSection === 'readings' || activeSection === 'cover') && (
            <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              <div className="border-b-2 border-[#7A470E] pb-3">
                <span className="text-[11px] font-bold text-[#7A470E] font-sc uppercase tracking-wider block">
                  Prescribed Doctoral Literature
                </span>
                <h2 className="text-2xl font-black font-sc text-[#0B2644]">
                  Recommended Reference Textbooks (5 Verified Texts)
                </h2>
              </div>

              <p className="text-xs text-slate-600 font-sans">
                Below are the official core reference textbooks specified in Dr. Ekere Jacob Daniel's course outline. Each item features verified ISBN credentials and live clickable hyperlinks to the official publisher catalogues and companion datasets.
              </p>

              <div className="space-y-4">
                {(ebook.generalReferences || []).map((r, rIdx) => (
                  <div key={rIdx} className="p-4 sm:p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#7A470E] transition-all space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-0.5 rounded bg-[#0B2644] text-white font-mono font-bold text-[10px]">
                            Text #{r.id || rIdx + 1}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-amber-100 text-[#7A470E] font-mono font-bold text-[10px] border border-amber-300">
                            ISBN: {r.isbn}
                          </span>
                        </div>
                        <h3 className="font-sc font-bold text-base sm:text-lg text-[#0B2644] leading-snug">
                          {r.title}
                        </h3>
                        <p className="text-xs text-slate-600 font-sans">
                          <strong>{r.author}</strong> ({r.year}) · <em>{r.edition}</em> · {r.publisher}
                        </p>
                      </div>
                    </div>

                    <p className="text-xs text-slate-700 bg-white p-3 rounded-xl border border-slate-200 leading-relaxed">
                      <strong>Scope & Pedagogical Focus:</strong> {r.scope}
                    </p>

                    {/* Verified Live External Hyperlinks */}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      {r.url && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#133562] text-white text-xs font-bold font-sc transition-colors cursor-pointer shadow-xs group"
                          title="Open official publisher page in a new window"
                        >
                          <BookOpen className="w-3.5 h-3.5 text-amber-300" />
                          <span>Official Publisher Portal</span>
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-transform" />
                        </a>
                      )}

                      {r.altUrl && (
                        <a
                          href={r.altUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-bold font-sc transition-colors cursor-pointer"
                          title="Open WorldCat or Companion resource"
                        >
                          <Database className="w-3.5 h-3.5 text-[#7A470E]" />
                          <span>WorldCat / Companion</span>
                          <ExternalLink className="w-3 h-3 text-slate-500" />
                        </a>
                      )}

                      {r.companionUrl && (
                        <a
                          href={r.companionUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-[#7A470E] border border-amber-300 text-xs font-bold font-sc transition-colors cursor-pointer"
                          title="Open author dataset and code repository"
                        >
                          <Code2 className="w-3.5 h-3.5" />
                          <span>Datasets & Code</span>
                          <ExternalLink className="w-3 h-3 text-[#7A470E]" />
                        </a>
                      )}

                      <button
                        onClick={() => copyToClipboard(r.citation, `cite-${rIdx}`)}
                        className="inline-flex items-center space-x-1 px-2.5 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-600 border border-slate-300 text-xs font-mono transition-colors cursor-pointer ml-auto"
                        title="Copy full APA citation"
                      >
                        {copiedLink === `cite-${rIdx}` ? (
                          <>
                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="text-emerald-700 font-bold">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 text-slate-400" />
                            <span>Copy Citation</span>
                          </>
                        )}
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 6: SOFTWARE LABORATORIES */}
          {(activeSection === 'software' || activeSection === 'cover') && (
            <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              <div className="border-b-2 border-[#7A470E] pb-3">
                <span className="text-[11px] font-bold text-[#7A470E] font-sc uppercase tracking-wider block">
                  Computing Environments
                </span>
                <h2 className="text-2xl font-black font-sc text-[#0B2644]">
                  Required Econometric Software & Script Libraries
                </h2>
              </div>

              <p className="text-xs text-slate-600 font-sans">
                Hands-on estimation is executed across three industry-standard econometric platforms. Doctoral candidates are provided configured virtual lab instances and script repositories.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(ebook.softwareTools || []).map((tool, idx) => (
                  <div key={idx} className="p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-amber-400 transition-colors flex flex-col justify-between space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <strong className="text-sm font-bold text-[#0B2644] font-sc">{tool.name}</strong>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-100 text-[#7A470E] font-bold">
                          {tool.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed font-sans">
                        {tool.desc}
                      </p>
                    </div>

                    <a
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1.5 text-xs font-bold font-sc text-[#0B2644] hover:text-[#7A470E] transition-colors pt-2 border-t border-slate-200 cursor-pointer"
                    >
                      <span>Official Documentation & Downloads</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 7: SENATE ASSESSMENT RUBRIC */}
          {(activeSection === 'assessment' || activeSection === 'cover') && (
            <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              <div className="border-b-2 border-[#7A470E] pb-3">
                <span className="text-[11px] font-bold text-[#7A470E] font-sc uppercase tracking-wider block">
                  Senate Grading Rubric
                </span>
                <h2 className="text-2xl font-black font-sc text-[#0B2644]">
                  Continuous Assessment & Examination Weightings
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {ebook.assessmentStructure.map((asg, aIdx) => (
                  <div key={aIdx} className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between gap-4">
                    <span className="text-xs sm:text-sm font-bold text-slate-800 font-sc">
                      {asg.component}
                    </span>
                    <span className="px-3 py-1 rounded-xl bg-[#0B2644] text-amber-200 font-mono font-bold text-xs shrink-0">
                      {asg.weight}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ======================================================== */}
        {/* BOTTOM ACTION FOOTER                                     */}
        {/* ======================================================== */}
        <div className="bg-[#030F1C] text-slate-400 px-4 sm:px-6 py-3 border-t border-slate-800 flex items-center justify-between text-xs shrink-0 print:hidden">
          <div className="flex items-center space-x-2 text-[11px] font-mono">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="truncate">AKSU Business School Official Course Dossier · 2025/2026</span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handlePrintPdf}
              className="flex items-center space-x-1 font-sc font-bold text-amber-300 hover:text-white transition-colors cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5 mr-1" />
              <span>Print / Save as PDF</span>
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={handleDownloadStandaloneHtml}
              className="flex items-center space-x-1 font-sc font-bold text-slate-300 hover:text-white transition-colors cursor-pointer"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              <span>HTML File</span>
            </button>
            <span className="text-slate-600">|</span>
            <button
              onClick={onClose}
              className="font-sc font-bold text-slate-400 hover:text-white transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CourseEbookModal;
